<template>
  <div>
    <SmsSender
      :messageTemplates="messageTemplates"
      :mobileNumber="bookingMobileNumber"
      :contactName="bookingFullName"
      :messageDialog="paxMessageDialog"
      :bookingNumber="selectedBooking.bookingId"
      :messageType="1"
      @closeSmsSender="closeSmsSender">
    </SmsSender>
    <!-- The driver view -->
    <v-data-table
      v-if="!!isDriverView && items.length>0"
      dense
      :headers="headers"
      :items="items"
      hide-default-footer
      disable-pagination
      :item-class="rowTextColor"
      class="elevation-1">
      <template v-slot:item.mobileNumber="{ item }">
        <v-btn
          v-if="item.mobileNumber"
          :title="`send text message - ${ item.mobileNumber }`"
          color="primary"
          dark
          x-small
          @click="setSelectedBooking(item)">
          <v-icon small>mdi-cellphone-message</v-icon>
        </v-btn>
        <v-btn
          :href="`tel:${ item.mobileNumber }`"
          :title="`call - ${ item.mobileNumber }`"
          v-if="item.mobileNumber"
          color="primary"
          dark
          x-small
          class="ml-1">
          <v-icon small>mdi-cellphone-wireless</v-icon>
        </v-btn>
        <span>{{item.mobileNumber}}</span>
      </template>
      <template v-slot:item.formattedAddress="{ item }">
        <a :href="item.googleMapsAddressLink" target="_blank">{{ item.formattedAddress }}</a>
        <ul id="otherAddress" class="other-address">
          <li :key="address.id" v-for="address in item.otherAddresses">
            <a :href="address.googleMapsAddressLink"
              target="_blank">{{ address.streetNumber }}
              {{ address.street}}
              {{ address.suburb }}</a>
          </li>
        </ul>
      </template>
      <template v-slot:item.flightDetails="{ item }">
        <a :href="getFlightUrl(item.flightNumber)" target="_blank">{{ item.flightDetails }}</a>
      </template>
    </v-data-table>

    <!-- The standard view -->
    <v-data-table
      v-if="!isDriverView"
      dense
      :headers="headers"
      :items="items"
      hide-default-footer
      disable-pagination
      :item-class="rowTextColor"
      class="elevation-1">
      <template v-slot:item.mobileNumber="{ item }">
        <v-btn
          v-if="item.mobileNumber"
          :title="`send text message - ${ item.mobileNumber }`"
          color="primary"
          dark
          x-small
          @click="setSelectedBooking(item)">
          <v-icon small>mdi-cellphone-message</v-icon>
        </v-btn>
        <v-btn
          :href="`tel:${ item.mobileNumber }`"
          :title="`call - ${ item.mobileNumber }`"
          v-if="item.mobileNumber"
          color="primary"
          dark
          x-small
          class="ml-1">
          <v-icon small>mdi-cellphone-wireless</v-icon>
        </v-btn>
        <span>{{item.mobileNumber}}</span>
      </template>
      <template v-slot:item.bookingId="{ item }">
        <a :href="getEditBookingUrl(item.bookingId)" target="_blank">{{ item.bookingId }}</a>
      </template>
      <template v-slot:item.flightDetails="{ item }">
        <a :href="getFlightUrl(item.flightNumber)" target="_blank">{{ item.flightDetails }}</a>
      </template>
      <template v-slot:item.toAirportBusId="{ item }" v-if="directionType===1">
        <v-select
          class="selectBus"
          v-model="item.toAirportBusId"
          :items="busList"
          item-text="busName"
          item-value="busId"
          label="Select"
          persistent-hint
          single-line
          dense
          hide-details="auto">
        </v-select>
      </template>
      <template v-slot:item.fromAirportBusId="{ item }" v-if="directionType===2">
        <v-select
          class="selectBus"
          v-model="item.fromAirportBusId"
          :items="busList"
          item-text="busName"
          item-value="busId"
          label="Select"
          persistent-hint
          single-line
          dense
          hide-details="auto">
        </v-select>
      </template>
      <template v-slot:item.formattedAddress="{ item }">
        <a :href="item.googleMapsAddressLink" target="_blank">{{ item.formattedAddress }}</a>
        <ul id="otherAddress" class="other-address">
          <li :key="address.id" v-for="address in item.otherAddresses">
            <a :href="address.googleMapsAddressLink"
              target="_blank">{{ address.streetNumber }}
              {{ address.street}}
              {{ address.suburb }}</a>
          </li>
        </ul>
      </template>
      <template v-slot:item.departStatus="{ item }" v-if="directionType===1">
        <v-checkbox
          v-model="item.departStatus"
          value="OK">
        </v-checkbox>
      </template>
      <template v-slot:item.arrivalStatus="{ item }" v-if="directionType===2">
        <v-checkbox
          v-model="item.arrivalStatus"
          value="OK">
        </v-checkbox>
      </template>
      <template v-slot:item.pickupTime="{ item }" v-if="directionType===1">
         <v-select
          class="pickupTimes"
          :items="pickupTimes"
          item-text="formatted"
          item-value="from"
          v-model="item.pickupTime"
          label="Select"
          persistent-hint
          single-line
          dense
          hide-details="auto">
            <template v-slot:selection="{ item }">
              <span>{{item.from}}-{{item.to}}</span>
            </template>
        </v-select>
      </template>
      <template v-slot:item.timeToAirport="{ item }" v-if="directionType===1">
        {{ $moment(item.timeToAirport, 'HH:mm:ss').format('HH:mm')}}
      </template>
      <template v-slot:item.fare="{ item }">
        {{item.fare | toCurrency}}
      </template>
      <template v-slot:item.commentsInbounds="{ item }" v-if="directionType===1">
        <v-textarea
            v-model="item.commentsInbounds"
            maxlength="150"
            hide-details="auto"
            rows="1"></v-textarea>
      </template>
      <template v-slot:item.commentsOutbounds="{ item }" v-if="directionType===2">
        <v-textarea
            v-model="item.commentsOutbounds"
            maxlength="150"
            hide-details="auto"
            rows="1"></v-textarea>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          @click="bookingAction(true, directionType, item)"
          v-if="(directionType===1 && !item.isActiveIn) || directionType===2 && !item.isActiveOut"
              x-small
              fab
              color="primary"
              dark>
          <v-icon>mdi-backup-restore</v-icon>
        </v-btn>
        <v-btn
          @click="bookingAction(false, directionType, item)"
          v-if="(directionType===1 && !!item.isActiveIn) || directionType===2 && !!item.isActiveOut"
              x-small
              fab
              color="error"
              red>
          <v-icon>mdi-cancel</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import storeConstants from '@/modules/bus-run-manager/store/storeConstants';
import rootStoreConstants from '@/store/storeConstants';
import SmsSender from '@/components/SmsSender.vue';

export default {
  name: 'BusRunBookingsControl',
  props: {
    bookingDate: {
      type: String,
      default: undefined,
    },
    items: {
      type: Array,
      default: undefined,
    },
    busList: {
      type: Array,
      default: undefined,
    },
    directionType: {
      type: Number,
      default: undefined,
    },
    headers: {
      type: Array,
      default: undefined,
    },
    index: {
      type: Number,
      default: undefined,
    },
    pickupTimes: {
      type: Array,
      default: undefined,
    },
    isDriverView: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    SmsSender,
  },
  data: () => ({
    bus: new Vue(),
    messageValid: false,
    paxMessageDialog: false,
    selectedBooking: {},
    selectedMessage: '',
    selectedMessageText: '',
    messageTemplates: [],
  }),
  computed: {
    ...mapGetters('busRunManagerModule', [
      storeConstants.BUS_SUMMARY,
      storeConstants.MESSAGE_TEMPLATES]),
    bookingFullName() {
      return this.selectedBooking?.fullName;
    },
    bookingMobileNumber() {
      return this.selectedBooking?.mobileNumber;
    },
  },
  methods: {
    ...mapActions('busRunManagerModule', [
      storeConstants.BOOKING_ACTION,
      storeConstants.SEND_PASSENGER_TEXT_MESSAGE,
      storeConstants.GET_MESSAGE_TEMPLATES]),
    bookingAction(action, directionType, booking) {
      const data = {
        action,
        direction: directionType,
        bookingId: booking.bookingId,
        bookingDate: this.bookingDate,
      };

      this.BOOKING_ACTION(data);
    },
    rowTextColor(item) {
      if ((item.transferType === 'OUT' && !item.isActiveOut) || (item.transferType === 'IN' && !item.isActiveIn)) {
        return 'booking-cancelled';
      }
      if (item.exclusive === 'Y') {
        return 'booking-private-transfer';
      }
      if (item.isAgentBooking) {
        return 'booking-agent';
      }

      return null;
    },
    getEditBookingUrl(bookingId) {
      return `editBooking/${bookingId}`;
    },
    getFlightUrl(flightNumber) {
      return `https://www.google.com/search?q=${flightNumber}`;
    },
    setSelectedBooking(item) {
      this.GET_MESSAGE_TEMPLATES()
        .then(() => {
          this.messageTemplates = this.MESSAGE_TEMPLATES;
          this.selectedBooking = item;
          this.paxMessageDialog = true;
          this.$store.commit(rootStoreConstants.SET_LOADING, false);
        }, (error) => {
          this.SET_ALERT_DETAILS({
            alertText: !!error.response && !!error.response.data
              ? error.response.data.error : error,
            showAlert: true,
          });
          this.$store.commit(rootStoreConstants.SET_LOADING, false);
        });
    },
    closeSmsSender() {
      this.paxMessageDialog = false;
      this.selectedBooking = {};
    },
  },
};
</script>
<style>
ul.other-address {
  padding-left: 10px;
  list-style: lower-roman;
  color: #023b02;
}
.v-input.selectBus, .v-checkbox.arrivalStatus, .v-input.pickupTimes  {
  font-size: inherit;
}

.pickupTimes label {
  font-size: 0.7rem;
}

.busRunControl .v-text-field input, .busRunControl .v-textarea textarea {
  font-size: 0.7em;
}
.busRunControl tbody tr:nth-of-type(odd) {
   background-color: rgba(0, 0, 0, .05);
}
.booking-cancelled,
.booking-cancelled a,
.booking-cancelled .v-select__slot .v-select__selection,
.booking-cancelled .v-select__slot .v-select__selections,
.booking-cancelled .v-input__slot .v-text-field__slot textarea {
  color: #ff0000;
  background-color: #fff5ee !important;
}
.booking-private-transfer,
.booking-private-transfer a,
.booking-private-transfer .v-select__slot .v-select__selection,
.booking-private-transfer .v-select__slot .v-select__selections,
.booking-private-transfer .v-input__slot .v-text-field__slot textarea {
  color: #0000ff;
  background-color: #f0fff0 !important;
}
.booking-agent,
.booking-agent a,
.booking-agent .v-select__slot .v-select__selection,
.booking-agent .v-select__slot .v-select__selections,
.booking-agent .v-input__slot .v-text-field__slot textarea {
  color: #008000;
  background-color: #f0ffff !important;
}
</style>
